import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { formatSurface } from '../../../../../../common-utils/formatters';
import { DPE_STEPS } from '../../../../../@types/DPE';
import { resetAll, setCurrentStep, setLastCompletionType, setNbOwner } from '../../../../../store/DpeReducer';
import { useAppDispatch, useAppSelector } from '../../../../../store/hook';
import type { RootState } from '../../../../../store/store';
import { JWTAuthorization } from '../../../../../utils/JWTHeader';
import Popup from '../../../../common/Popup';
import Spinner from '../../../../common/Spinner';
import { DPE_QUALIF_STEPS } from '../dpeQualifStep';

interface DpeStep1Props {
  isDpeTreated: boolean;
  area?: number;
}

interface AlreadySentInfo {
  lastSentAt: string;
  canSendFrom: string;
}

function DpeStep1({ isDpeTreated = false, area }: DpeStep1Props) {
  const dispatch = useAppDispatch();
  const selectedDpe = useAppSelector((state) => state.dpe.selectedDpe);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [loading, setLoading] = useState<boolean | string>(false);
  const [nbBienInputValue, setNbBienInputValue] = useState('');
  const [nbBiens, setNbBiens] = useState<number | null>(null);
  const [alreadySentInfo, setAlreadySentInfo] = useState<AlreadySentInfo | null>(null);

  const formatFrDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleDpeGeneralMailingButton = async() => {
    if (!selectedDpe?.idCopro || !selectedDpe?.id) {
      return;
    }
    setLoading('dpeGeneralMailing');
    try {
      const statusResponse = await fetch(`/api/copros/${selectedDpe.idCopro}/dpe-general-mailing/status`, {
        method: 'GET',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
      });

      if (!statusResponse.ok) {
        throw new Error(`Status check failed: ${statusResponse.status}`);
      }

      const status = await statusResponse.json();

      if (status.canSendNow === false) {
        setAlreadySentInfo({
          lastSentAt: status.lastSentAt,
          canSendFrom: status.canSendFrom,
        });
        setLoading(false);
        return;
      }

      const sendResponse = await fetch(`/api/copros/${selectedDpe.idCopro}/dpe-general-mailing/sends`, {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ triggeringDpeId: selectedDpe.id }),
      });

      if (!sendResponse.ok) {
        if (sendResponse.status === 409) {
          // Race: another request started the cooldown between our /status
          // check and the POST. Re-query /status which is guaranteed to
          // expose fresh dates and render the cooldown modal accordingly.
          const refreshedStatusResponse = await fetch(`/api/copros/${selectedDpe.idCopro}/dpe-general-mailing/status`, {
            method: 'GET',
            headers: {
              Authorization: JWTAuthorization(jwt),
              'Content-Type': 'application/json',
            },
          });
          const refreshedStatus = refreshedStatusResponse.ok
            ? await refreshedStatusResponse.json()
            : status;

          setAlreadySentInfo({
            lastSentAt: refreshedStatus.lastSentAt ?? status.lastSentAt,
            canSendFrom: refreshedStatus.canSendFrom ?? status.canSendFrom,
          });
          setLoading(false);
          return;
        }
        throw new Error(`Send registration failed: ${sendResponse.status}`);
      }

      setLoading(false);
      dispatch(setLastCompletionType('dpeGeneralMailing'));
      dispatch(setCurrentStep(DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED));
    } catch (error) {
      console.error('Error triggering DPE general mailing:', error);
      setLoading(false);
    }
  };

  const handleValidButton = () => {
    dispatch(setCurrentStep(DPE_QUALIF_STEPS.COMPLETING_OWNERS_DETAILS));
  };

  const handleNoOwnerButton = () => {
    setLoading('noOwner');
    fetch(`/api/dpes/${selectedDpe.id}/treat`, {
      method: 'PATCH',
      headers: {
        Authorization: JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
    }).then((_response) => _response.json())
      .then(() => {
        fetch(`/api/dpes/${selectedDpe.id}`, {
          method: 'PATCH',
          headers: {
            Authorization: JWTAuthorization(jwt),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ step: DPE_STEPS.NO_OWNER }),
        }).then((_response) => _response.json())
          .then(() => {
            setLoading(false);
            dispatch(setLastCompletionType('noOwner'));
            dispatch(setCurrentStep(DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED));
          }).catch((error) => {
            console.error('Error treating dpe :', error);
          });
      }).catch((error) => {
        console.error('Error treating dpe :', error);
      });
  };

  const handleVoluntarilyUntreatedButton = () => {
    setLoading('voluntarilyUntreated');
    fetch(`/api/dpes/${selectedDpe.id}/treat`, {
      method: 'PATCH',
      headers: {
        Authorization: JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
    }).then((_response) => _response.json())
      .then(() => {
        fetch(`/api/dpes/${selectedDpe.id}`, {
          method: 'PATCH',
          headers: {
            Authorization: JWTAuthorization(jwt),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ step: DPE_STEPS.VOLUNTARILY_UNTREATED }),
        }).then((_response) => _response.json())
          .then(() => {
            setLoading(false);
            dispatch(setLastCompletionType('voluntarilyUntreated'));
            dispatch(setCurrentStep(DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED));
          }).catch((error) => {
            console.error('Error treating dpe :', error);
          });
      }).catch((error) => {
        console.error('Error treating dpe :', error);
      });
  };

  const isButtonDisabled = !(nbBiens > 0);

  useEffect(() => {
    // Update store when nbBiens changes
    if (nbBiens !== null) {
      dispatch(setNbOwner(nbBiens));
    }
  }, [nbBiens, dispatch]);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setNbBienInputValue(value);

    if (value === '') {
      setNbBiens(null);
    } else {
      // if input is NaN, set it to 0
      if (isNaN(value)) {
        setNbBiens(0);
        setNbBienInputValue('0');
        return;
      }
      const numValue = Math.min(Math.max(Number(value), 0), 50);
      // check if the value is a number
      setNbBiens(numValue);
    }
  };

  return (
    <div className="flex grow flex-col">
      { !isDpeTreated
        ? (
          <div>
            <div className="mb-5 mb-10 text-lg">
              <span className="text-xl font-bold">Ce que vous devez faire : </span>
              {' '}
              Identifiez si les caractéristiques du DPE, rappelées ci-dessus, vous
              permettent d'identifier des lots au sein de la copropriété qui seraient à l'origine du diagnostique de performance énergétique.
            </div>
            <div className="grow" />
            <div>
              <div className="mr-2 text-lg">
                {/* Needed otherwise it outputs a 0 */}
                {(area != 0 && area != null && area != undefined) && (
                  <>
                    D'après vous, combien de lots avec une surface estimative de
                    {' '}
                    <b>{formatSurface(area)}</b>
                    {' '}
                    peuvent correspondre dans la copropriété indiquée ?
                  </>
                )}
                {(area == 0 || area == null || area == undefined) && (
                  <>
                    D'après vous, combien de lots avec ces critères peuvent correspondre dans la copropriété indiquée ?
                  </>
                )}
              </div>
              <input
                type="number"
                name="nbBiens"
                id="nbBiens"
                className="my-6 rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset"
                min="0"
                max="50"
                value={nbBienInputValue}
                onChange={handleInputChange}
              />
            </div>
            <div className="grow" />
            <div className="justify-center md:grid md:grid-cols-5 md:gap-4">
              <button
                type="button"
                className="mt-5 w-full rounded-xl bg-red-600 px-5 py-2 text-center text-lg font-semibold text-white shadow-sm"
                onClick={handleVoluntarilyUntreatedButton}
              >
                {loading === 'voluntarilyUntreated' ? (
                  <>
                    <Spinner aria-label="Center-aligned spinner" size="lg" className="mr-1" />
                    Chargement...
                  </>
                ) : 'Je ne souhaite pas traiter ce DPE' }
              </button>
              <button
                type="button"
                className="mt-5 w-full rounded-xl bg-orange-500 px-5 py-2 text-center text-lg font-semibold text-white shadow-sm"
                onClick={handleNoOwnerButton}
              >
                {loading === 'noOwner' ? (
                  <>
                    <Spinner aria-label="Center-aligned spinner" size="lg" className="mr-1" />
                    Chargement...
                  </>
                ) : 'Je n\'ai pas trouvé de lots correspondant' }
              </button>
              <button
                type="button"
                className="mt-5 w-full rounded-xl bg-purple-700 px-5 py-2 text-center text-lg font-semibold text-white shadow-sm"
                onClick={handleDpeGeneralMailingButton}
              >
                {loading === 'dpeGeneralMailing' ? (
                  <>
                    <Spinner aria-label="Center-aligned spinner" size="lg" className="mr-1" />
                    Chargement...
                  </>
                ) : 'Envoyer à tous les copropriétaires de l\'immeuble' }
              </button>
              <button
                type="button"
                className="mt-5 w-full rounded-xl bg-rgpd-blue-dark px-5 py-2 text-center text-lg font-semibold text-white shadow-sm"
                onClick={() => { dispatch(resetAll()); }}
              >
                Annuler l'identification
              </button>
              <button
                type="button"
                className={`mt-5 w-full rounded-xl bg-rgpd-green px-5 py-2 text-center text-lg font-semibold text-white shadow-sm ${isButtonDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                onClick={handleValidButton}
                disabled={isButtonDisabled}
              >
                {loading === 'validate' ? (
                  <>
                    <Spinner aria-label="Center-aligned spinner" size="lg" className="mr-1" />
                    Chargement...
                  </>
                ) : 'Compléter les informations du ou des propriétaire(s) potentiel(s)' }
              </button>
            </div>
            <Popup
              isOpen={alreadySentInfo !== null}
              onClose={() => setAlreadySentInfo(null)}
              title="Envoi général déjà préparé pour cette copropriété"
              additionalClass="w-full max-w-xl"
            >
              {alreadySentInfo !== null && (
                <div className="space-y-3 text-base leading-relaxed text-gray-700">
                  <p>
                    Pour éviter de sur-solliciter les copropriétaires, un envoi général ne peut être
                    préparé qu&apos;une fois tous les 2 mois par copropriété.
                  </p>
                  <p>
                    Un envoi général a déjà été préparé pour cette copropriété le
                    {' '}
                    <strong>{formatFrDate(alreadySentInfo.lastSentAt)}</strong>
                    .
                  </p>
                  <p>
                    Vous pourrez en relancer un à partir du
                    {' '}
                    <strong>{formatFrDate(alreadySentInfo.canSendFrom)}</strong>
                    .
                  </p>
                  <p className="text-sm text-gray-500">
                    En attendant, vous pouvez retrouver et compléter l&apos;envoi déjà préparé depuis
                    l&apos;onglet «&nbsp;Envois généraux DPE&nbsp;» de la Communication aux propriétaires.
                  </p>
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      className="rounded-xl bg-rgpd-blue-dark px-5 py-2 text-center text-base font-semibold text-white shadow-sm hover:brightness-110"
                      onClick={() => setAlreadySentInfo(null)}
                    >
                      J&apos;ai compris
                    </button>
                  </div>
                </div>
              )}
            </Popup>
          </div>
        )
        : (
          <div>
            <div className="mr-2 text-lg">
              {area && (
                <>
                  D'après vous, combien de lots avec une surface estimative de
                  {' '}
                  <b>{formatSurface(area)}</b>
                  {' '}
                  peuvent correspondre dans la copropriété indiquée ?
                </>
              )}
              {!area && (
                <>
                  D'après vous, combien de lots avec ces critères peuvent correspondre dans la copropriété indiquée ?
                </>
              )}
            </div>
            <input
              type="number"
              name="nbBiens"
              id="nbBiens"
              className="my-6 rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset"
              min="0"
              max="50"
              value={nbBienInputValue}
              onChange={handleInputChange}
            />
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                className={`w-auto rounded-xl bg-rgpd-green px-5 py-3 text-center text-lg font-semibold text-white shadow-sm ${isButtonDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                onClick={handleValidButton}
                disabled={isButtonDisabled}
              >
                {loading === 'validate' ? (
                  <>
                    <Spinner aria-label="Center-aligned spinner" size="lg" className="mr-1" />
                    Chargement...
                  </>
                ) : 'Compléter les informations du ou des propriétaire(s) potentiel(s)' }
              </button>
            </div>
          </div>
        )}
    </div>
  );
}

export default DpeStep1;
